import { useMutation, useQuery } from '@apollo/client';

import { MARK_NOTIFICATION_READ } from '../graphql/mutations';
import { NOTIFICATIONS_QUERY } from '../graphql/queries';
import type { Notification, UserError } from '../types/notifications.types';

/**
 * The full notification history for the "View all" page. Unlike the bell drawer — which
 * loads a single newest-first page on open — this pages through the whole list with "Load more", reusing
 * the same offset-paginated `notifications` query the backend already exposes (no new server surface).
 *
 * "Load more" uses Apollo `fetchMore` with an `updateQuery` that appends the next page to the accumulated
 * items, so the list grows in place. Marking a row read goes through the shared `MarkNotificationRead`
 * mutation, which returns `{ id, isRead, readAt }` — Apollo normalises it by id, so the row flips in place
 * without collapsing the paged list; only the unread-count badge is refetched.
 */
export function useNotificationHistory(pageSize = 20) {
  const { data, loading, error, fetchMore, refetch, networkStatus } = useQuery(NOTIFICATIONS_QUERY, {
    variables: { unreadOnly: false, page: 1, pageSize },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const [markReadMutation] = useMutation(MARK_NOTIFICATION_READ, {
    // Only the badge needs a refetch; the row itself updates via the normalised entity the mutation returns,
    // so the accumulated page list is never re-fetched (and never collapses back to page 1).
    refetchQueries: ['UnreadNotificationCount'],
  });

  const list = data?.notifications ?? null;
  const items: Notification[] = list?.items ?? [];
  const currentPage = list?.page ?? 1;
  const hasNextPage = list?.hasNextPage ?? false;

  // networkStatus 3 === fetchMore in flight (Apollo NetworkStatus.fetchMore).
  const loadingMore = networkStatus === 3;

  const loadMore = async () => {
    if (!hasNextPage || loadingMore) return;
    await fetchMore({
      variables: { unreadOnly: false, page: currentPage + 1, pageSize },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.notifications) return prev;
        const merged = [
          ...(prev.notifications?.items ?? []),
          ...fetchMoreResult.notifications.items,
        ];
        return { notifications: { ...fetchMoreResult.notifications, items: merged } };
      },
    });
  };

  const markRead = async (notificationId: string): Promise<UserError[]> => {
    const { data: result } = await markReadMutation({ variables: { input: { notificationId } } });
    return result?.markNotificationRead.errors ?? [];
  };

  return {
    notifications: items,
    totalCount: list?.totalCount ?? 0,
    hasNextPage,
    loadMore,
    loadingMore,
    markRead,
    loading: loading && !data,
    error: error ?? null,
    refetch,
  };
}
